import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home, ShoppingCart, SlidersHorizontal, Search, X,
  ChevronDown, ChevronUp, Plus, Minus, Package,
  MapPin, Globe, User, ShoppingBag, Tag, Menu,
} from "lucide-react";

import logo from "../../../assets/cosmeta/logo.png";

import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  listenToPublishedClassified,
  filterLocalProductsByCountry,
  setFilters,
} from "../../../redux/classifiedsSlice";
import { addToCart, decrementCartItem } from "../../../redux/cartSlice";
import { Notifications, SupportAgent, ChatBubble, ShoppingCartOutlined } from "@mui/icons-material";
import {
  listenToUserNotifications,
  stopUserNotificationsListener,
} from "../../../redux/notificationsSlice";
import { useCurrency } from "@cosmoola/react-cosmoola-js";
import {
  electronicsColorAttributes,
  electronicsSizeAttributes,
} from "../../ATTRIBUTES/ElectronicsAttributes";

/* ═══════════════════════════════════════════════════════
   PHONES CATEGORY IDS
═══════════════════════════════════════════════════════ */
const PHONE_CATEGORY_IDS = [20000001];

/* ═══════════════════════════════════════════════════════
   DESIGN TOKENS
═══════════════════════════════════════════════════════ */
const T = {
  bg:           "#FFFFFF",
  bgSoft:       "#F7F9FF",
  bgMuted:      "#EEF4FF",
  black:        "#060D1F",
  border:       "#DCE8FF",
  muted:        "#6B8CB8",
  mutedSoft:    "#A8C4D8",
  blue:         "#0A66C2",
  blueLight:    "#5BCBFF",
  blueDim:      "rgba(10,102,194,0.10)",
  gold:         "#FEBA02",
  red:          "#E50914",
  globalRed:    "#CC0000",
  grad:         "linear-gradient(135deg,#003580 0%,#0061fe 60%,#FEBA02 100%)",
  gradText:     "linear-gradient(90deg,#0A66C2,#5BCBFF)",
  navy:         "#0A0F1E",
  navyMid:      "#111827",
  orangeOutline:"1.5px solid rgba(10,102,194,0.35)",
};

const GRADIENT = T.grad;
const NAVY     = T.navy;

/* ═══════════════════════════════════════════════════════
   COSMIC CSS
═══════════════════════════════════════════════════════ */
const COSMIC_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  ::-webkit-scrollbar { display: none; }
  input, button { font-family: inherit; }

  @keyframes gradSpin {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  @keyframes aurora {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  @keyframes shimmerGold {
    0%   { background-position: -200% center; }
    100% { background-position:  200% center; }
  }
  @keyframes orb1 {
    0%   { transform: translate(0,0) scale(1); }
    33%  { transform: translate(60px,-40px) scale(1.1); }
    66%  { transform: translate(-30px,50px) scale(0.95); }
    100% { transform: translate(0,0) scale(1); }
  }
  @keyframes orb2 {
    0%   { transform: translate(0,0); }
    33%  { transform: translate(-70px,30px) scale(1.08); }
    66%  { transform: translate(40px,-60px) scale(1.04); }
    100% { transform: translate(0,0); }
  }
  @keyframes starTwinkle {
    0%,100% { opacity:0.15; transform:scale(0.8); }
    50%     { opacity:1;    transform:scale(1.4); }
  }
  @keyframes mc-pulse { 0%,100%{opacity:1} 50%{opacity:.3} }

  .cosmic-bg {
    background: linear-gradient(135deg,
      #060010 0%, #100020 12%, #001828 28%,
      #0a0a30 44%, #001a10 60%, #180028 76%,
      #0a0015 90%, #060010 100%
    );
    background-size: 400% 400%;
    animation: aurora 16s ease infinite;
  }

  .shimmer-title {
    background: linear-gradient(90deg,
      #0A66C2 0%, #5BCBFF 20%, #fff 40%,
      #FEBA02 60%, #5BCBFF 80%, #0A66C2 100%
    );
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: shimmerGold 4s linear infinite;
  }

  .running-grad-line {
    background: linear-gradient(90deg, #003580, #0061fe, #FEBA02, #5BCBFF, #003580);
    background-size: 300% 100%;
    animation: gradSpin 4s linear infinite;
  }

  .phone-card-hover {
    transition: transform 0.3s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.3s ease;
  }
  .phone-card-hover:hover {
    transform: translateY(-6px);
    box-shadow: 0 24px 56px rgba(10,102,194,0.22), 0 0 0 2px rgba(10,102,194,0.4) !important;
  }

  .sort-item:hover { background: #EEF4FF; }
  input::placeholder { color: rgba(255,255,255,0.4); }
`;

/* ═══════════════════════════════════════════════════════
   STARS
═══════════════════════════════════════════════════════ */
const STARS = Array.from({ length: 28 }, (_, i) => ({
  id: i,
  size: Math.random() * 2.5 + 0.6,
  x: Math.random() * 100,
  y: Math.random() * 100,
  delay: Math.random() * 6,
  dur: 2 + Math.random() * 4,
}));

/* ═══════════════════════════════════════════════════════
   SORT OPTIONS
═══════════════════════════════════════════════════════ */
type SortKey = "default" | "price_asc" | "price_desc" | "newest" | "name_asc";
const SORT_OPTS: { key: SortKey; label: string }[] = [
  { key: "default",    label: "Best Match"       },
  { key: "newest",     label: "New Arrivals"      },
  { key: "price_asc",  label: "Price: Low → High" },
  { key: "price_desc", label: "Price: High → Low" },
  { key: "name_asc",   label: "Name: A → Z"       },
];

/* ═══════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════ */
const Phones: React.FC = () => {
  const dispatch  = useDispatch<AppDispatch>();
  const navigate  = useNavigate();
  const { convertRaw, symbol } = useCurrency();

  /* ── selectors ── */
  const authUser         = useSelector((s: RootState) => s.auth.user);
  const authLoaded       = useSelector((s: RootState) => s.auth.authLoaded);
  const userId           = authUser?.userId;
  const notifications    = useSelector((s: RootState) => s.notifications.notifications);
  const unreadCount      = useMemo(() => notifications.filter((n: any) => !n.read).length, [notifications]);
  const cartItems        = useSelector((s: RootState) => s.cart.items);
  const filteredListings = useSelector((s: RootState) => s.classifieds.filteredListings);
  const cartTotal        = cartItems.reduce((s: number, i: any) => s + i.qty, 0);

  /* ── ui state ── */
  const [isMobile,      setIsMobile]      = useState(window.innerWidth <= 768);
  const [searchQuery,   setSearchQuery]   = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [activeSort,    setActiveSort]    = useState<SortKey>("default");
  const [sortOpen,      setSortOpen]      = useState(false);
  const [navOpen,       setNavOpen]       = useState(false);
  const sortBtnRef = useRef<HTMLButtonElement>(null);
  const [sortAnchor, setSortAnchor] = useState<{ top: number; left: number } | null>(null);

  /* ── location state ── */
  const [userCountry,    setUserCountry]    = useState<string | null>(null);
  const [userCity,       setUserCity]       = useState<string | null>(null);
  const [locationDenied, setLocationDenied] = useState(false);
  const [locationLoading,setLocationLoading]= useState(true);
  const [showGlobal,     setShowGlobal]     = useState(false);

  /* ── variant state ── */
  const [variantOpen,   setVariantOpen]   = useState(false);
  const [variantItem,   setVariantItem]   = useState<any | null>(null);
  const [variantMode,   setVariantMode]   = useState<"fashion" | "beauty" | null>(null);
  const [selectedColor, setSelectedColor] = useState<number | null>(null);
  const [selectedSize,  setSelectedSize]  = useState<number | null>(null);
  const [variantQty,    setVariantQty]    = useState(1);

  /* ── effects ── */
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    fn(); window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    if (!authLoaded) return;
    if (!authUser || !userId) navigate("/AuthSwitch");
  }, [authLoaded, authUser, userId, navigate]);

  useEffect(() => { dispatch(listenToPublishedClassified()); }, [dispatch]);

  useEffect(() => {
    if (!userId) return;
    dispatch(listenToUserNotifications(userId));
    return () => { dispatch(stopUserNotificationsListener()); };
  }, [dispatch, userId]);

  /* ── geolocation ── */
  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationDenied(true); setLocationLoading(false);
      dispatch(filterLocalProductsByCountry(null)); return;
    }
    const detect = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLocationDenied(false);
          if ((window as any).google?.maps) {
            const gc = new (window as any).google.maps.Geocoder();
            gc.geocode(
              { location: { lat: pos.coords.latitude, lng: pos.coords.longitude } },
              (results: any, status: string) => {
                if (status === "OK" && results?.[0]) {
                  const comps = results[0].address_components;
                  const get   = (t: string) => comps.find((c: any) => c.types.includes(t))?.long_name ?? null;
                  setUserCity(get("locality") || get("administrative_area_level_2"));
                  const country = get("country"); setUserCountry(country);
                  if (country) dispatch(filterLocalProductsByCountry(country));
                }
                setLocationLoading(false);
              }
            );
          } else { setLocationLoading(false); }
        },
        (err) => {
          if (err.code === err.PERMISSION_DENIED) setLocationDenied(true);
          setLocationLoading(false); dispatch(filterLocalProductsByCountry(null));
        }
      );
    };
    detect();
    if ("permissions" in navigator) {
      navigator.permissions.query({ name: "geolocation" as PermissionName }).then(r => {
        r.onchange = () => { if (r.state === "granted") detect(); };
      });
    }
  }, [dispatch]);

  useEffect(() => {
    if (!userCountry) return;
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry));
  }, [userCountry, showGlobal, dispatch]);

  /* ── handlers ── */
  const handleToggleGlobal = () => {
    const next = !showGlobal; setShowGlobal(next);
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalProductsByCountry(next ? null : userCountry));
  };

  const getQty = (id: string) => cartItems.find((i: any) => i.listingId === id)?.qty || 0;

  const getVariantMode = (item: any): "fashion" | "beauty" | null => {
    if (!item.variants || !Object.keys(item.variants).length) return null;
    if (Object.values(item.variants).some((v: any) => Array.isArray(v.sizeIds) && v.sizeIds.length)) return "fashion";
    if (Object.values(item.variants).some((v: any) => v.imageUrl && (!v.sizeIds || !v.sizeIds.length))) return "beauty";
    return null;
  };

  const handleAddToCart = (item: any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (getVariantMode(item) !== null) {
      setVariantItem(item); setVariantMode(getVariantMode(item));
      setSelectedColor(null); setSelectedSize(null); setVariantQty(1); setVariantOpen(true);
      return;
    }
    dispatch(addToCart({ listingId: item.listingId, name: item.name!, price: item.rate!, photo: item.photos?.[0], hostId: item.hostId, brandId: item.brandId }));
  };

  const canContinue =
    variantMode === null ||
    (variantMode === "fashion" && selectedColor !== null && selectedSize !== null && variantQty > 0) ||
    (variantMode === "beauty"  && selectedColor !== null && variantQty > 0);

  /* ── previewColorId — drives thumbnail in modal ── */
  const previewColorId = useMemo(() => {
    if (!variantItem || !variantMode) return null;
    const colors = variantItem.attributes?.[variantMode === "beauty" ? 10001 : 20002];
    if (!Array.isArray(colors) || !colors.length) return null;
    return selectedColor ?? colors[0];
  }, [variantItem, variantMode, selectedColor]);

  /* ── auto-select first colour for beauty mode ── */
  useEffect(() => {
    if (variantOpen && variantMode === "beauty" && variantItem && selectedColor === null) {
      const colors = variantItem.attributes?.[10001];
      if (Array.isArray(colors) && colors.length) setSelectedColor(colors[0]);
    }
  }, [variantOpen, variantMode, variantItem, selectedColor]);

  const handleVariantContinue = (toCheckout: boolean) => {
    if (!variantItem) return;
    dispatch(addToCart({
      listingId: variantItem.listingId, name: variantItem.name!, price: variantItem.rate!,
      photo: variantItem.variants?.[selectedColor!]?.imageUrl ?? variantItem.photos?.[0],
      colorId: selectedColor!, ...(variantMode === "fashion" && { sizeId: selectedSize! }),
      qty: variantQty, hostId: variantItem.hostId, brandId: variantItem.brandId,
    }));
    setVariantOpen(false); setVariantItem(null); setSelectedColor(null); setSelectedSize(null); setVariantQty(1);
    if (toCheckout) navigate(`/metacheckout/${userId}`, {
      state: { userId, totalQty: cartItems.reduce((s: number, i: any) => s + i.qty, 0) + variantQty, items: cartItems },
    });
  };

  const goToCheckout = () => {
    if (!cartItems.length) { alert("Your cart is empty"); return; }
    navigate(`/metacheckout/${userId}`, {
      state: {
        userId,
        totalQty: cartItems.reduce((s: number, i: any) => s + i.qty, 0),
        totalPrice: cartItems.reduce((s: number, i: any) => s + i.qty * i.price, 0),
        items: cartItems,
      },
    });
  };

  const goToDetails = (id: string) => navigate(`/metacommercedetails/${id}`);

  const fmtPrice = (rawRate: number) => {
    const converted = convertRaw(rawRate, "EUR");
    return `${symbol}${converted.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const toggleSort = () => {
    if (sortOpen) { setSortOpen(false); setSortAnchor(null); return; }
    if (sortBtnRef.current) {
      const r = sortBtnRef.current.getBoundingClientRect();
      setSortAnchor({ top: r.bottom + 6, left: r.left });
    }
    setSortOpen(true);
  };

  /* ── filtered products ── */
  const phoneProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list = filteredListings.filter((l: any) => {
      if (!l.isPublished) return false;
      if (!PHONE_CATEGORY_IDS.includes(l.categoryId)) return false;
      if (q && !`${l.name ?? ""} ${l.description ?? ""}`.toLowerCase().includes(q)) return false;
      return true;
    });
    if (activeSort === "price_asc")  list = [...list].sort((a: any, b: any) => (a.rate ?? 0) - (b.rate ?? 0));
    if (activeSort === "price_desc") list = [...list].sort((a: any, b: any) => (b.rate ?? 0) - (a.rate ?? 0));
    if (activeSort === "name_asc")   list = [...list].sort((a: any, b: any) => (a.name ?? "").localeCompare(b.name ?? ""));
    if (activeSort === "newest")     list = [...list].sort((a: any, b: any) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
    return list;
  }, [filteredListings, searchQuery, activeSort]);

  /* ════════════════════════════════════════════════════════
     HEADER
  ════════════════════════════════════════════════════════ */
  const Header = () => (
    <div style={{ position: "sticky", top: 0, zIndex: 200 }}>
      <div className="cosmic-bg" style={{ position: "relative", overflow: "hidden" }}>
        {/* stars */}
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          {STARS.slice(0, 14).map(s => (
            <div key={s.id} style={{ position: "absolute", left: `${s.x}%`, top: `${s.y}%`, width: s.size, height: s.size, borderRadius: "50%", background: "rgba(255,255,255,0.8)", animation: `starTwinkle ${s.dur}s ease-in-out ${s.delay}s infinite` }} />
          ))}
        </div>
        {/* orbs */}
        <div style={{ position: "absolute", top: "-40%", left: "10%", width: 180, height: 180, borderRadius: "50%", background: "radial-gradient(circle,rgba(10,102,194,0.35) 0%,transparent 70%)", filter: "blur(30px)", animation: "orb1 12s ease-in-out infinite", pointerEvents: "none" }} />
        <div style={{ position: "absolute", top: "-30%", right: "15%", width: 140, height: 140, borderRadius: "50%", background: "radial-gradient(circle,rgba(254,186,2,0.25) 0%,transparent 70%)", filter: "blur(25px)", animation: "orb2 15s ease-in-out infinite", pointerEvents: "none" }} />

        {/* Row 1 */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px 6px", position: "relative", zIndex: 2 }}>
          <div onClick={() => navigate(`/MetaCommerce/${userId}`)} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", flexShrink: 0 }}>
            <div style={{ padding: 2, borderRadius: 10, background: T.grad, display: "flex" }}>
              <img src={logo} alt="CosMeta"
                style={{ width: 26, height: 26, borderRadius: 8, background: "#fff", objectFit: "contain", padding: 3 }}
                onError={e => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
            </div>
          </div>

          <span className="shimmer-title" style={{ fontFamily: "'Playfair Display',serif", fontSize: isMobile ? 16 : 20, fontWeight: 700, letterSpacing: -0.3, flexShrink: 0 }}>
            Phones
          </span>

          {!locationLoading && !locationDenied && userCountry && (
            <button onClick={handleToggleGlobal}
              style={{ display: "flex", alignItems: "center", gap: 5, background: showGlobal ? "rgba(255,255,255,.06)" : "rgba(91,203,255,.12)", border: `1px solid ${showGlobal ? "rgba(255,255,255,.18)" : "rgba(91,203,255,.35)"}`, borderRadius: 8, padding: "5px 10px", color: showGlobal ? "rgba(255,255,255,.55)" : T.blueLight, fontSize: 11, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>
              {showGlobal ? <Globe size={11} /> : <MapPin size={11} />}
              <span style={{ display: isMobile ? "none" : "inline" }}>{showGlobal ? "Global" : userCity || userCountry}</span>
            </button>
          )}
          {locationLoading && (
            <div style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(255,255,255,.06)", borderRadius: 8, padding: "5px 10px", flexShrink: 0 }}>
              <div style={{ width: 5, height: 5, borderRadius: "50%", background: T.blueLight, animation: "mc-pulse 1.2s infinite" }} />
              <span style={{ fontSize: 10, color: "rgba(255,255,255,.45)" }}>Locating…</span>
            </div>
          )}

          <div style={{ flex: 1 }} />

          <button onClick={() => navigate(`/MetaCommerce/${userId}`)}
            style={{ background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.18)", borderRadius: 8, padding: "7px 10px", cursor: "pointer", display: "flex", alignItems: "center", color: "#fff", flexShrink: 0 }}>
            <Home size={16} />
          </button>

          <div style={{ position: "relative", cursor: "pointer", flexShrink: 0 }} onClick={() => navigate(`/notifications/${userId}`)}>
            <Notifications style={{ fontSize: 22, color: T.blueLight }} />
            {unreadCount > 0 && (
              <span style={{ position: "absolute", top: -4, right: -4, background: T.globalRed, color: "#fff", fontSize: 8, fontWeight: 900, width: 15, height: 15, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {unreadCount}
              </span>
            )}
          </div>

          <button onClick={goToCheckout}
            style={{ position: "relative", background: cartTotal > 0 ? T.blue : "rgba(255,255,255,.08)", border: `1px solid ${cartTotal > 0 ? "transparent" : "rgba(255,255,255,.18)"}`, borderRadius: 8, padding: "7px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 5, color: "#fff", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
            <ShoppingCart size={16} />
            {!isMobile && cartTotal > 0 && <span>{cartTotal}</span>}
            {cartTotal > 0 && (
              <span style={{ position: "absolute", top: -4, right: -4, background: "#EF4444", color: "#fff", fontSize: 8, fontWeight: 900, width: 15, height: 15, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {cartTotal > 99 ? "99+" : cartTotal}
              </span>
            )}
          </button>

          <button onClick={() => setNavOpen(v => !v)}
            style={{ background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.18)", borderRadius: 8, padding: "7px 10px", cursor: "pointer", display: "flex", alignItems: "center", color: "#fff", flexShrink: 0 }}>
            {navOpen ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>

        {/* Row 2 — search + sort */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 16px 12px", position: "relative", zIndex: 2 }}>
          <div style={{ display: "flex", alignItems: "center", flex: 1, background: "rgba(255,255,255,.10)", borderRadius: 10, overflow: "hidden", border: `1.5px solid ${searchFocused ? "rgba(91,203,255,.7)" : "rgba(255,255,255,.15)"}`, backdropFilter: "blur(8px)", boxShadow: searchFocused ? "0 0 0 3px rgba(10,102,194,.2)" : "none", transition: "border-color .2s,box-shadow .2s" }}>
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              placeholder="Search phones…"
              style={{ flex: 1, border: "none", outline: "none", padding: "10px 14px", fontSize: 13, color: "#fff", background: "transparent" }}
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery("")} style={{ background: "none", border: "none", cursor: "pointer", padding: "0 8px", color: "rgba(255,255,255,.5)" }}>
                <X size={13} />
              </button>
            )}
            <div style={{ width: 42, height: 42, background: T.grad, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Search size={15} color="#fff" />
            </div>
          </div>

          <button ref={sortBtnRef} onClick={toggleSort}
            style={{ display: "flex", alignItems: "center", gap: 5, background: sortOpen ? T.blue : "rgba(255,255,255,.08)", border: `1px solid ${sortOpen ? T.blue : "rgba(255,255,255,.18)"}`, borderRadius: 8, padding: "10px 12px", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap" }}>
            <SlidersHorizontal size={13} />
            {!isMobile && <span>{activeSort !== "default" ? SORT_OPTS.find(o => o.key === activeSort)?.label?.split(":")[0] : "Sort"}</span>}
            {sortOpen ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
          </button>
        </div>

        <div className="running-grad-line" style={{ height: 2, position: "relative", zIndex: 2 }} />
      </div>

      {/* Sort dropdown */}
      {sortOpen && sortAnchor && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 9998 }} onClick={() => { setSortOpen(false); setSortAnchor(null); }} />
          <div style={{ position: "fixed", top: sortAnchor.top, left: sortAnchor.left, background: "#fff", borderRadius: 14, boxShadow: "0 20px 56px rgba(0,0,0,.22)", zIndex: 9999, minWidth: 200, overflow: "hidden", border: T.orangeOutline }}>
            {SORT_OPTS.map(opt => (
              <div key={opt.key} className="sort-item"
                onClick={() => { setActiveSort(opt.key); setSortOpen(false); setSortAnchor(null); }}
                style={{ padding: "11px 18px", fontSize: 13, fontWeight: activeSort === opt.key ? 700 : 500, color: activeSort === opt.key ? T.blue : NAVY, background: activeSort === opt.key ? T.bgMuted : "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #F1F5F9" }}>
                {opt.label}{activeSort === opt.key && <span style={{ color: T.blue }}>✓</span>}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Nav dropdown */}
      <AnimatePresence>
        {navOpen && (
          <>
            <div style={{ position: "fixed", inset: 0, zIndex: 599 }} onClick={() => setNavOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              style={{ position: "fixed", top: 70, right: 16, width: 220, zIndex: 600, background: "#fff", borderRadius: 18, boxShadow: "0 24px 64px rgba(0,0,0,.20)", border: T.orangeOutline, overflow: "hidden" }}>
              {[
                { label: "Profile",  icon: <User size={14} />,                       path: `/profile/${userId}` },
                { label: "Orders",   icon: <ShoppingBag size={14} />,                 path: `/MetaClientDashboard/${userId}` },
                { label: "Sell",     icon: <Tag size={14} />,                         path: `/metaonboarding/${userId}` },
                { label: "Support",  icon: <SupportAgent style={{ fontSize: 14 }} />, path: `/CustomerSupport/${userId}` },
                { label: "Chat",     icon: <ChatBubble style={{ fontSize: 14 }} />,   path: "/MessagesPreview" },
              ].map(item => (
                <button key={item.label}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 18px", cursor: "pointer", fontSize: 13, fontWeight: 700, color: NAVY, borderBottom: "1px solid #F1F5F9", background: "none", width: "100%", textAlign: "left", border: "none" }}
                  onClick={() => { navigate(item.path); setNavOpen(false); }}>
                  <span style={{ color: T.blue }}>{item.icon}</span>{item.label}
                </button>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );

  /* ════════════════════════════════════════════════════════
     HERO STRIP
  ════════════════════════════════════════════════════════ */
  const HeroStrip = () => (
    <div style={{ position: "relative", overflow: "hidden", background: "#0A0F1E", padding: isMobile ? "28px 16px 24px" : "40px 32px 32px" }}>
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        {STARS.map(s => (
          <div key={s.id} style={{ position: "absolute", left: `${s.x}%`, top: `${s.y}%`, width: s.size, height: s.size, borderRadius: "50%", background: "rgba(255,255,255,0.7)", animation: `starTwinkle ${s.dur}s ease-in-out ${s.delay}s infinite` }} />
        ))}
      </div>
      <div style={{ position: "absolute", top: "20%", left: "5%", width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle,rgba(10,102,194,0.28) 0%,transparent 70%)", filter: "blur(60px)", animation: "orb1 14s ease-in-out infinite", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: "-10%", right: "8%", width: 240, height: 240, borderRadius: "50%", background: "radial-gradient(circle,rgba(254,186,2,0.22) 0%,transparent 70%)", filter: "blur(50px)", animation: "orb2 18s ease-in-out infinite", pointerEvents: "none" }} />

      <div style={{ position: "relative", zIndex: 2 }}>
        <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          style={{ fontSize: 9, letterSpacing: "0.32em", color: T.gold, fontFamily: "'DM Sans',sans-serif", fontWeight: 600, marginBottom: 10 }}>
          ⚡ PHONES COLLECTION
        </motion.p>

        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="shimmer-title"
          style={{ fontFamily: "'Playfair Display',serif", fontSize: isMobile ? "clamp(26px,7vw,36px)" : "clamp(36px,4vw,56px)", fontWeight: 700, lineHeight: 1.08, letterSpacing: "-0.02em", marginBottom: 10 }}>
          Latest Smartphones
        </motion.h1>

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }}
          style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", fontFamily: "'DM Sans',sans-serif", fontWeight: 300, maxWidth: 480 }}>
          {locationDenied ? "Showing all global phones" : showGlobal ? "Browsing worldwide inventory" : userCity ? `Local listings near ${userCity}` : "Discovering phones near you"}
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
          style={{ display: "flex", gap: 24, marginTop: 20, flexWrap: "wrap" }}>
          <div>
            <span style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: "#fff", fontFamily: "'Playfair Display',serif" }}>{phoneProducts.length}</span>
            <span style={{ fontSize: 10, color: "rgba(255,255,255,.4)", marginLeft: 6, fontFamily: "'DM Sans',sans-serif", letterSpacing: "0.1em" }}>LISTINGS</span>
          </div>
          {!locationDenied && !showGlobal && userCountry && (
            <div>
              <span style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: T.blueLight, fontFamily: "'Playfair Display',serif" }}>{userCountry}</span>
              <span style={{ fontSize: 10, color: "rgba(255,255,255,.4)", marginLeft: 6, fontFamily: "'DM Sans',sans-serif", letterSpacing: "0.1em" }}>LOCAL</span>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );

  /* ════════════════════════════════════════════════════════
     PHONE CARD
  ════════════════════════════════════════════════════════ */
  const PhoneCard = ({ item }: { item: any }) => {
    const qty      = getQty(item.listingId);
    const price    = fmtPrice(item.rate ?? 0);
    const discount = typeof item.discount === "number" && item.discount > 0 ? item.discount : null;
    const [hovered, setHovered] = useState(false);

    /* ── MOBILE ── */
    if (isMobile) {
      return (
        <motion.div whileTap={{ scale: 0.95 }}
          style={{ borderRadius: 18, overflow: "hidden", border: T.orangeOutline, boxShadow: `0 4px 18px ${T.blueDim}`, background: "#fff", cursor: "pointer", display: "flex", flexDirection: "column" }}
          onClick={() => goToDetails(item.listingId)}>
          <div style={{ width: "100%", aspectRatio: "1/1", position: "relative", overflow: "hidden", background: `linear-gradient(135deg,${T.blue}22,${T.blue}08)` }}>
            {item.photos?.[0] ? (
              <img src={item.photos[0]} alt={item.name}
                style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform .35s cubic-bezier(.34,1.56,.64,1)" }}
                onMouseEnter={e => (e.currentTarget.style.transform = "scale(1.10)")}
                onMouseLeave={e => (e.currentTarget.style.transform = "scale(1)")} />
            ) : (
              <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Package size={34} color={T.blue} />
              </div>
            )}
            <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top,${T.navy}EE,transparent 52%)` }} />
            {discount && (
              <div style={{ position: "absolute", top: 7, left: 7, background: T.red, color: "#fff", fontSize: 9, fontWeight: 800, padding: "2px 7px", borderRadius: 6, zIndex: 5 }}>
                {discount}% OFF
              </div>
            )}
            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "8px 10px 10px", zIndex: 3, pointerEvents: "none" }}>
              <div style={{ fontWeight: 800, fontSize: 10, color: "#fff", lineHeight: 1.25, marginBottom: 2, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden", textShadow: "0 1px 4px rgba(0,0,0,.5)" }}>
                {item.name}
              </div>
              <span style={{ color: T.red, fontWeight: 900, fontSize: 16, letterSpacing: -0.3 }}>{price}</span>
            </div>
            <div style={{ position: "absolute", top: 7, right: 7, zIndex: 10 }} onClick={e => e.stopPropagation()}>
              {qty > 0 ? (
                <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                  style={{ display: "flex", alignItems: "center", gap: 0, background: "rgba(0,97,254,.92)", borderRadius: 20, padding: "4px 6px", backdropFilter: "blur(8px)", border: "1.5px solid rgba(255,255,255,.2)" }}>
                  <button style={{ width: 26, height: 26, borderRadius: "50%", border: "none", background: "rgba(255,255,255,.18)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                    onClick={e => { e.stopPropagation(); dispatch(decrementCartItem({ listingId: item.listingId })); }}>
                    <Minus size={11} color="#fff" />
                  </button>
                  <span style={{ color: "#fff", fontWeight: 900, minWidth: 20, textAlign: "center", fontSize: 12 }}>{qty}</span>
                  <button style={{ width: 26, height: 26, borderRadius: "50%", border: "none", background: "rgba(255,255,255,.28)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                    onClick={e => { e.stopPropagation(); handleAddToCart(item); }}>
                    <Plus size={11} color="#fff" />
                  </button>
                </motion.div>
              ) : (
                <motion.button whileTap={{ scale: 0.88 }}
                  onClick={e => { e.stopPropagation(); handleAddToCart(item); }}
                  style={{ width: 38, height: 38, borderRadius: "50%", border: "none", background: T.red, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 2px 10px rgba(229,9,20,.5)" }}>
                  <ShoppingCartOutlined style={{ fontSize: 18, color: "#fff" }} />
                </motion.button>
              )}
            </div>
          </div>
        </motion.div>
      );
    }

    /* ── DESKTOP ── */
    return (
      <motion.div className="phone-card-hover"
        style={{ borderRadius: 20, overflow: "hidden", border: T.orangeOutline, boxShadow: `0 6px 28px ${T.blueDim}`, background: "#fff", cursor: "pointer", display: "flex", flexDirection: "column" }}
        onClick={() => goToDetails(item.listingId)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}>
        <div style={{ width: "100%", aspectRatio: "1/1", position: "relative", overflow: "hidden", background: `linear-gradient(135deg,${T.blue}18,${T.blue}06)` }}>
          {item.photos?.[0] ? (
            <img src={item.photos[0]} alt={item.name}
              style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform .38s cubic-bezier(.34,1.56,.64,1)", transform: hovered ? "scale(1.08)" : "scale(1)" }} />
          ) : (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Package size={44} color={T.blue} />
            </div>
          )}
          <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top,${NAVY}F0 0%,${NAVY}88 38%,transparent 68%)`, pointerEvents: "none" }} />
          {discount && (
            <div style={{ position: "absolute", top: 8, left: 8, background: T.red, color: "#fff", fontSize: 10, fontWeight: 800, padding: "3px 8px", borderRadius: 7, zIndex: 5, boxShadow: "0 2px 8px rgba(229,9,20,.5)" }}>
              {discount}% OFF
            </div>
          )}

          <div style={{ position: "absolute", top: 8, right: 8, zIndex: 10 }} onClick={e => e.stopPropagation()}>
            {qty > 0 ? (
              <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                style={{ display: "flex", alignItems: "center", background: "rgba(0,97,254,.92)", borderRadius: 22, padding: "3px 5px", backdropFilter: "blur(8px)", border: "1.5px solid rgba(255,255,255,.22)", gap: 0 }}>
                <button style={{ width: 30, height: 30, borderRadius: "50%", border: "none", background: "rgba(255,255,255,.18)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                  onClick={e => { e.stopPropagation(); dispatch(decrementCartItem({ listingId: item.listingId })); }}>
                  <Minus size={12} color="#fff" />
                </button>
                <span style={{ color: "#fff", fontWeight: 900, minWidth: 24, textAlign: "center", fontSize: 14 }}>{qty}</span>
                <button style={{ width: 30, height: 30, borderRadius: "50%", border: "none", background: "rgba(255,255,255,.28)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                  onClick={e => { e.stopPropagation(); handleAddToCart(item); }}>
                  <Plus size={12} color="#fff" />
                </button>
              </motion.div>
            ) : (
              <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                onClick={e => { e.stopPropagation(); handleAddToCart(item); }}
                style={{ width: 46, height: 46, borderRadius: "50%", border: "none", background: T.red, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 3px 12px rgba(229,9,20,.55)" }}>
                <ShoppingCartOutlined style={{ fontSize: 22, color: "#fff" }} />
              </motion.button>
            )}
          </div>

          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "12px 14px 14px", zIndex: 3, pointerEvents: "none" }}>
            <div style={{ fontWeight: 800, fontSize: 12, color: "#fff", lineHeight: 1.25, marginBottom: 5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden", textShadow: "0 1px 4px rgba(0,0,0,.6)", letterSpacing: -0.2 }}>
              {item.name}
            </div>
            <span style={{ color: T.red, fontWeight: 900, fontSize: 22, letterSpacing: -0.5, textShadow: "0 1px 0 rgba(100,0,0,.5), 0 2px 12px rgba(229,9,20,.4)", lineHeight: 1 }}>
              {price}
            </span>
          </div>
        </div>
      </motion.div>
    );
  };

  /* ════════════════════════════════════════════════════════
     VARIANT MODAL — full attributes support
  ════════════════════════════════════════════════════════ */
  const VariantModal = () => (
    <AnimatePresence>
      {variantOpen && variantItem && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={() => setVariantOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 999, display: "flex", alignItems: "flex-end", justifyContent: "center", backdropFilter: "blur(8px)" }}>
          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 30, stiffness: 300 }}
            onClick={e => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: "28px 28px 0 0", padding: 24, width: "100%", maxWidth: 480, maxHeight: "90vh", overflowY: "auto" }}>

            {/* drag handle */}
            <div style={{ width: 44, height: 4, borderRadius: 2, background: "#E2E8F0", margin: "0 auto 22px" }} />

            {/* product preview */}
            <div style={{ display: "flex", gap: 14, marginBottom: 20 }}>
              {variantItem.photos?.[0] && (
                <div style={{ width: 72, height: 72, borderRadius: 14, overflow: "hidden", background: "#F8FAFC", flexShrink: 0 }}>
                  <img
                    src={variantItem.variants?.[previewColorId!]?.imageUrl ?? variantItem.photos[0]}
                    alt={variantItem.name}
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                  />
                </div>
              )}
              <div>
                <h3 style={{ margin: "0 0 4px", fontWeight: 800, fontSize: 15, color: NAVY }}>{variantItem.name}</h3>
                <span style={{ color: T.red, fontWeight: 900, fontSize: 18 }}>{fmtPrice(variantItem.rate ?? 0)}</span>
              </div>
            </div>

            {/* colour + size selection */}
            {variantMode && (
              <>
                <h4 style={{ margin: "0 0 10px", fontSize: 12, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.8 }}>
                  {variantMode === "beauty" ? "Select Shade" : "Select Color"}
                </h4>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
                  {Object.keys(variantItem.variants || {}).map(id => {
                    const colorId = Number(id);
                    const color   = electronicsColorAttributes.values.find((v: any) => v.id === colorId);
                    return (
                      <button key={colorId}
                        onClick={() => { setSelectedColor(colorId); if (variantMode === "fashion") setSelectedSize(null); }}
                        style={{
                          width: 34, height: 34, borderRadius: "50%",
                          border: selectedColor === colorId ? `3px solid ${NAVY}` : "2px solid #ddd",
                          background: color?.hexCodes?.[0] ?? "#eee",
                          cursor: "pointer",
                          transform: selectedColor === colorId ? "scale(1.15)" : "scale(1)",
                          boxShadow: selectedColor === colorId ? `0 0 0 2px #fff,0 0 0 4px ${T.blue}` : "none",
                          transition: "all .15s",
                        }}
                      />
                    );
                  })}
                </div>

                {/* size row — fashion mode only */}
                {variantMode === "fashion" && selectedColor !== null && (
                  <>
                    <h4 style={{ margin: "0 0 10px", fontSize: 12, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.8 }}>
                      Select Size
                    </h4>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
                      {(variantItem.variants?.[selectedColor]?.sizeIds ?? []).map((sizeId: number) => {
                        const size = electronicsSizeAttributes.values.find((v: any) => v.id === sizeId);
                        return (
                          <button key={sizeId} onClick={() => setSelectedSize(sizeId)}
                            style={{
                              padding: "8px 16px", borderRadius: 10,
                              border: `2px solid ${selectedSize === sizeId ? NAVY : "#DCE8FF"}`,
                              fontWeight: 700, fontSize: 13, cursor: "pointer",
                              background: selectedSize === sizeId ? NAVY : "#fff",
                              color: selectedSize === sizeId ? "#fff" : NAVY,
                              transition: "all .15s",
                            }}>
                            {size?.name}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </>
            )}

            {/* qty stepper — show once valid selection made */}
            {selectedColor !== null && (variantMode === "beauty" || (variantMode === "fashion" && selectedSize !== null)) && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 20, marginBottom: 20, background: "#F8FAFC", borderRadius: 14, padding: "12px 16px" }}>
                <button onClick={() => setVariantQty(q => Math.max(1, q - 1))}
                  style={{ width: 38, height: 38, borderRadius: 10, border: "2px solid #E2E8F0", background: "#fff", display: "grid", placeItems: "center", cursor: "pointer" }}>
                  <Minus size={14} color={NAVY} />
                </button>
                <span style={{ fontSize: 22, fontWeight: 900, color: NAVY, minWidth: 30, textAlign: "center" }}>{variantQty}</span>
                <button onClick={() => setVariantQty(q => q + 1)}
                  style={{ width: 38, height: 38, borderRadius: 10, border: `2px solid ${T.blue}`, background: T.blue, display: "grid", placeItems: "center", cursor: "pointer" }}>
                  <Plus size={14} color="#fff" />
                </button>
              </div>
            )}

            {/* CTA buttons */}
            <div style={{ display: "flex", gap: 10 }}>
              <button disabled={!canContinue} onClick={() => handleVariantContinue(false)}
                style={{ flex: 1, padding: 14, borderRadius: 14, border: `2px solid ${canContinue ? NAVY : "#E2E8F0"}`, background: canContinue ? NAVY : "#F8FAFC", color: canContinue ? "#fff" : "#94A3B8", fontWeight: 800, fontSize: 14, cursor: canContinue ? "pointer" : "not-allowed" }}>
                Add to Cart
              </button>
              <button disabled={!canContinue} onClick={() => handleVariantContinue(true)}
                style={{ flex: 1, padding: 14, borderRadius: 14, border: "none", background: canContinue ? GRADIENT : "#F8FAFC", color: canContinue ? "#fff" : "#94A3B8", fontWeight: 800, fontSize: 14, cursor: canContinue ? "pointer" : "not-allowed" }}>
                Buy Now
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  /* ════════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════════ */
  return (
    <div style={{ minHeight: "100vh", background: "#F4F6FA", paddingBottom: 32, fontFamily: "'DM Sans',sans-serif" }}>
      <style>{COSMIC_CSS}</style>

      <Header />
      <HeroStrip />

      <div style={{ padding: isMobile ? "20px 12px" : "28px 28px" }}>

        {/* results bar */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 4, height: 22, borderRadius: 2, background: GRADIENT }} />
            <span style={{ fontWeight: 900, fontSize: 15, color: NAVY }}>
              {locationDenied ? "All Phones" : showGlobal ? "Global Phones" : userCity ? `Phones near ${userCity}` : userCountry ?? "All Phones"}
            </span>
            <span style={{ fontSize: 12, color: "#94A3B8", background: "#F1F5F9", padding: "2px 9px", borderRadius: 20 }}>
              {phoneProducts.length}
            </span>
          </div>
          {!locationDenied && userCountry && (
            <button onClick={handleToggleGlobal}
              style={{ display: "flex", alignItems: "center", gap: 5, background: showGlobal ? "#F1F5F9" : "#EFF6FF", borderRadius: 20, padding: "5px 13px", border: `1.5px solid ${showGlobal ? "#E2E8F0" : "#BFDBFE"}`, fontSize: 11, fontWeight: 700, color: showGlobal ? "#64748B" : "#2563EB", cursor: "pointer" }}>
              {showGlobal ? <MapPin size={11} /> : <Globe size={11} />}
              {showGlobal ? "Show Local" : "Show Global"}
            </button>
          )}
        </div>

        {/* product grid */}
        {phoneProducts.length > 0 ? (
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(auto-fill,minmax(200px,1fr))", gap: isMobile ? 12 : 18 }}>
            {phoneProducts.map((item: any) => <PhoneCard key={item.listingId} item={item} />)}
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "72px 24px" }}>
            <div style={{ display: "inline-block", marginBottom: 24 }}>
              <div style={{ width: 80, height: 80, borderRadius: "50%", background: "linear-gradient(135deg,#003580,#0061fe)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 8px 32px rgba(10,102,194,.35)" }}>
                <span style={{ fontSize: 36 }}>📱</span>
              </div>
            </div>
            <h3 style={{ fontWeight: 900, color: NAVY, marginBottom: 8, fontSize: 18, fontFamily: "'Playfair Display',serif" }}>
              {locationDenied ? "Location needed" : "No phones found"}
            </h3>
            <p style={{ color: "#94A3B8", marginBottom: 24, fontSize: 13 }}>
              {locationDenied
                ? "Enable location to see local phones."
                : searchQuery
                  ? `No results for "${searchQuery}"`
                  : "No phones listed in your area yet."}
            </p>
            <button
              onClick={() => { setSearchQuery(""); setActiveSort("default"); if (!showGlobal) handleToggleGlobal(); }}
              style={{ padding: "12px 28px", borderRadius: 12, border: "none", background: GRADIENT, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 13 }}>
              Browse Global Phones
            </button>
          </div>
        )}
      </div>

      <VariantModal />
    </div>
  );
};

export default Phones;